import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Share,
  Linking,
  KeyboardAvoidingView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals, primaryAlpha } from '@/constants/theme';
import { useInviteMember } from '@/hooks/useHousehold';

const INVITE_EMAIL_SUBJECT = 'Join my household on Sage Chef';

export default function InviteScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { inviteMember, loading: creating, error: inviteError } = useInviteMember();

  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copyDone, setCopyDone] = useState(false);
  const [email, setEmail] = useState('');
  const [emailBlurred, setEmailBlurred] = useState(false);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  const emailError =
    emailBlurred && email.trim() !== '' && !isValidEmail(email)
      ? 'Please enter a valid email address'
      : undefined;

  useEffect(() => {
    let cancelled = false;
    inviteMember()
      .then((result) => {
        if (!cancelled && result?.link) setInviteLink(result.link);
      })
      .catch(() => {
        if (!cancelled) setInviteLink(null);
      });
    return () => {
      cancelled = true;
    };
  }, [inviteMember]);

  const handleCopyLink = async () => {
    if (!inviteLink) return;
    if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(inviteLink);
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 2000);
    } else {
      try {
        await Share.share({
          message: `Join my household on Sage Chef: ${inviteLink}`,
          url: inviteLink,
          title: 'Invite to household',
        });
      } catch {
        // Share cancelled or not available
      }
    }
  };

  const handleSendByEmail = () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailBlurred(true);
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailBlurred(true);
      return;
    }
    if (!inviteLink) return;
    const body = `Join my household on Sage Chef by opening this link:\n\n${inviteLink}`;
    const mailto = `mailto:${encodeURIComponent(trimmed)}?subject=${encodeURIComponent(INVITE_EMAIL_SUBJECT)}&body=${encodeURIComponent(body)}`;
    Linking.openURL(mailto);
  };

  const showError = inviteError && !inviteLink && !creating;

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: Math.max(insets.top, 16) },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => router.back()}
            accessibilityLabel="Go back"
          >
            <Ionicons name="arrow-back" size={20} color={Brand.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>

          <View style={styles.header}>
            <View style={styles.iconContainer}>
              <Ionicons name="person-add" size={48} color={Brand.primary} />
            </View>
            <Text style={styles.title}>Invite a member</Text>
            <Text style={styles.subtitle}>
              Share the invite link or send it by email. They can open the link in the app or paste it under Settings → Join household.
            </Text>
          </View>

          {creating && !inviteLink && (
            <View style={[styles.card, styles.cardCentered]}>
              <ActivityIndicator size="large" color={Brand.primary} />
              <Text style={styles.loadingText}>Creating invite link…</Text>
            </View>
          )}

          {showError && (
            <View style={styles.card}>
              <Text style={styles.errorText}>{inviteError.message}</Text>
              <Button variant="outline" onPress={() => router.back()} style={styles.backToSettings}>
                Back to Settings
              </Button>
            </View>
          )}

          {inviteLink && (
            <>
              <View style={styles.card}>
                <Text style={styles.label}>Invite link</Text>
                <Text style={styles.linkText} testID="invite-link-text" selectable numberOfLines={3}>
                  {inviteLink}
                </Text>
                <Button
                  onPress={handleCopyLink}
                  style={styles.copyButton}
                >
                  {copyDone ? 'Copied' : Platform.OS === 'web' ? 'Copy link' : 'Share link'}
                </Button>
              </View>

              <View style={styles.card}>
                <Text style={styles.label}>Send by email</Text>
                <Text style={styles.hint}>
                  Enter their email to open your mail app with the invite link pre-filled.
                </Text>
                <Input
                  value={email}
                  onChangeText={(t) => {
                    setEmail(t);
                    setEmailBlurred(false);
                  }}
                  onBlur={() => setEmailBlurred(true)}
                  placeholder="email@example.com"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  style={styles.input}
                />
                {emailError && <Text style={styles.fieldError}>{emailError}</Text>}
                <Button
                  variant="outline"
                  onPress={handleSendByEmail}
                  style={styles.sendButton}
                >
                  Send invite by email
                </Button>
              </View>
            </>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginBottom: 24,
    paddingVertical: 4,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  header: {
    marginBottom: 32,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: primaryAlpha(0.2),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    lineHeight: 24,
  },
  card: {
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 20,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.06)',
    marginBottom: 16,
  },
  cardCentered: {
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: Neutrals.textSecondary,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginBottom: 8,
    alignSelf: 'stretch',
  },
  linkText: {
    fontSize: 14,
    color: Neutrals.textSecondary,
    marginBottom: 16,
  },
  copyButton: {},
  hint: {
    fontSize: 14,
    color: Neutrals.textSecondary,
    marginBottom: 12,
    lineHeight: 20,
  },
  input: {
    marginBottom: 8,
  },
  fieldError: {
    fontSize: 14,
    color: Brand.destructive,
    marginBottom: 12,
  },
  sendButton: {
    marginTop: 4,
  },
  backToSettings: {
    marginTop: 8,
  },
  errorText: {
    fontSize: 16,
    color: Neutrals.textPrimary,
    marginBottom: 16,
  },
});
